/**
 * Non-appointment actions: named events, breaks, travel, absences, plan
 * exceptions and group umbrellas. The legacy menu's seven near-identical
 * "Löschen + confirm" cases collapse into small declarative objects around
 * ONE confirm dialog; only message and verb differ.
 */
import moment from "moment";
import { Ban, Pencil, Trash2, Undo2 } from "lucide-react";
import {
  AbsenceCtx,
  ActionServices,
  BreakCtx,
  CalendarAction,
  CancelledDayCtx,
  GroupCtx,
  NamedEventCtx,
  TravelCtx,
  WorkingHoursCtx,
  forType,
} from "./types";

const confirmDelete = (
  services: ActionServices,
  message: string,
  onConfirm: () => void | Promise<unknown>,
) => services.openDialog({ kind: "confirm", title: "Löschen", message, onConfirm });

export const editEventTitle: CalendarAction<NamedEventCtx> = {
  id: "editEventTitle",
  group: "modify",
  icon: Pencil,
  label: "Titel bearbeiten",
  when: forType("namedEvent"),
  run: (ctx, services) =>
    services.openDialog({
      kind: "editEventTitle",
      eventId: String(ctx.raw.id ?? ctx.event.id),
      initialTitle: ctx.title,
      isContinuous: ctx.isContinuous,
    }),
};

export const deleteNamedEvent: CalendarAction<NamedEventCtx> = {
  id: "deleteNamedEvent",
  group: "danger",
  icon: Trash2,
  label: "Löschen",
  hotkey: "d",
  when: forType("namedEvent"),
  run: (ctx, services) =>
    confirmDelete(
      services,
      `Sind Sie sicher, dass Sie den Eintrag '${ctx.title}' löschen möchten?`,
      () => {
        const id = String(ctx.raw.id ?? ctx.event.id);
        return ctx.isContinuous
          ? services.controller()?.onRemoveContinuousEvent(id)
          : services.controller()?.onRemoveEvent(id, ctx.raw);
      },
    ),
};

export const deleteBreak: CalendarAction<BreakCtx> = {
  id: "deleteBreak",
  group: "danger",
  icon: Trash2,
  label: "Löschen",
  hotkey: "d",
  when: forType("break"),
  run: (ctx, services) =>
    confirmDelete(
      services,
      `Möchten Sie die Pause für diesen Tag entfernen? Dies betrifft nur den ${moment(
        ctx.raw.start,
      ).format("DD.MM.YYYY")}.`,
      // onRemoveEvent delegates lunch breaks to the exception flow internally.
      () => services.controller()?.onRemoveEvent(String(ctx.raw.id ?? ctx.event.id), ctx.raw),
    ),
};

export const deleteTravel: CalendarAction<TravelCtx> = {
  id: "deleteTravel",
  group: "danger",
  icon: Trash2,
  label: "Löschen",
  hotkey: "d",
  when: forType("travel"),
  run: (ctx, services) =>
    confirmDelete(
      services,
      `Sind Sie sicher, dass Sie den Eintrag '${ctx.event.title || "Weg"}' löschen möchten?`,
      () => services.controller()?.onRemoveEvent(String(ctx.raw.id ?? ctx.event.id), ctx.raw),
    ),
};

export const deleteAbsence: CalendarAction<AbsenceCtx> = {
  id: "deleteAbsence",
  group: "danger",
  icon: Trash2,
  label: "Löschen",
  hotkey: "d",
  when: forType("absence"),
  run: (ctx, services) =>
    confirmDelete(
      services,
      `Sind Sie sicher, dass Sie den Eintrag '${ctx.event.title ?? ""}' löschen möchten?`,
      () => services.controller()?.onRemoveEvent(String(ctx.raw.id ?? ctx.event.id), ctx.raw),
    ),
};

export const resetCancelledDay: CalendarAction<CancelledDayCtx> = {
  id: "resetCancelledDay",
  group: "danger",
  icon: Undo2,
  label: "Plan zurücksetzen",
  when: forType("cancelledDay", (ctx) => !!ctx.exceptionId),
  run: (ctx, services) =>
    confirmDelete(
      services,
      "Sind Sie sicher, dass Sie die Absage zurücknehmen möchten? Die ursprünglichen Arbeitszeiten werden wiederhergestellt.",
      () => services.deleteWorkHourException(ctx.exceptionId!),
    ),
};

/**
 * Working hours: only EXCEPTIONS are deletable ("Plan zurücksetzen").
 * Standard plan blocks get the legacy info toast instead of a dead menu —
 * the item stays visible so the answer to "why can't I delete this?" is one
 * click away, exactly like the old calendar.
 */
export const resetWorkingHoursException: CalendarAction<WorkingHoursCtx> = {
  id: "resetWorkingHoursException",
  group: "danger",
  icon: Undo2,
  label: "Plan zurücksetzen",
  when: forType("workingHours"),
  run: (ctx, services) => {
    if (!ctx.exceptionId) {
      services.notify(
        "Standard-Arbeitszeiten können nicht gelöscht werden. Sie können sie nur ändern oder den ganzen Tag absagen.",
        "info",
      );
      return;
    }
    confirmDelete(
      services,
      `Sind Sie sicher, dass Sie die Ausnahme '${ctx.event.title ?? ""}' löschen möchten? Die ursprünglichen Arbeitszeiten werden wiederhergestellt.`,
      () => services.deleteWorkHourException(ctx.exceptionId!),
    );
  },
};

export const deleteGroupTherapist: CalendarAction<GroupCtx> = {
  id: "deleteGroupTherapist",
  group: "danger",
  icon: Ban,
  label: (ctx) =>
    ctx.participantNames.length > 0
      ? `Gruppe löschen (${ctx.participantNames.length} Teilnehmer)`
      : "Gruppe löschen",
  hotkey: "d",
  when: forType("group", (ctx) => !!ctx.timeSlotId),
  run: (ctx, services) =>
    confirmDelete(
      services,
      `Sind Sie sicher, dass Sie den Eintrag '${ctx.event.title ?? ""}' löschen möchten?`,
      () => services.controller()?.onRemoveGroupTherapist(ctx.timeSlotId!),
    ),
};
